import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ApplicationListView } from "../config/pendingApplicationView";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  isActionAllowed?: boolean;
  daysLeftForAction?: number | null;
  rl_application_type?: string;
  current_phase?: string;
};

export const level1ApplicationsKeyStore = {
  savedDemandNoteData: (...args: (number | string | boolean | undefined)[]) => [
    "level-1-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-1-application-last-review", id],
  allReviews: (id?: number | string) => ["level-1-application-all-reviews", id],
  id: (arg: number | string) => ["level-1-application", arg],
  decisionMaps: (arg?: number | string) => ["level-1-decision-maps", arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications",
    ...args,
  ],
  stats: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications-stats",
    ...args,
  ],
  certificateData: (
    applicationId?: number | string,
    reviewId?: number | string
  ) => ["level-1-certificate-data", applicationId, reviewId],
};

export function useApplicationStatsForLevel1({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/level-1-application-approval/stats", { filters }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationCountForLevel1({
  search,
  filters,
  view,
}: {
  search?: string;
  filters?: ApplicationFilterState;
  /** "pending" (only what this level must act on) or "all"; left out, the server lists all. */
  view?: ApplicationListView;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.counts(search || "", filters || {}, view || "all"),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-1-application-approval/count", { search, filters, view }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationsForLevel1({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
  view,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
  /** "pending" (only what this level must act on) or "all"; left out, the server lists all. */
  view?: ApplicationListView;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {},
      view || "all"
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TApplication[];
      }>("/level-1-application-approval/list", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
        view,
      }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationForLevel1({ id }: { id: number | string }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/level-1-application-approval/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLevel1DecisionMaps({ id }: { id: undefined | number }) {
  return useQuery({
    enabled: !!id,
    queryKey: level1ApplicationsKeyStore.decisionMaps(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          nocDecisions: Record<string, boolean>;
          documentDecisions: Record<string, boolean>;
        };
      }>(`/level-1-application-approval/decision-map/${id}`),
    select: (res) => res?.data?.data,
  });
}

export function useLevel1UpdateDocumentDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      docId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-1-application-approval/update-doc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel1UpdateNocDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      nocId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-1-application-approval/update-noc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel1LastReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          fieldsToChange?: number[];
          review?: { comment_text?: string; id?: number };
        };
      }>(`/level-1-application-approval/get-current-review/${id}`),
    select: (result) => result.data?.data,
  });
}

export type TReviewWithReviewer = {
  id: number;
  date: string;
  comment_text: string;
  level: string;
  reviewer?: { name: string; role: string };
};

export function useLevel1AllReviews({ id }: { id?: number }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.allReviews(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>(`/level-1-application-approval/get-all-reviews/${id}`),
    select: (result) => result.data?.data,
  });
}

export function useSaveLevel1Review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      comment: string;
      fieldsToChange: number[];
    }) => _axios.post("/level-1-application-approval/save-review", data),
    onSuccess: () => SuccessToast("Saved!"),
    onError: () => ErrorToast("Something went wrong while saving review!"),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.lastReview(applicationId),
      });
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.allReviews(applicationId),
      });
    },
  });
}

export function useLevel1ReturnApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      user_id: number;
      application_id: string;
    }) => _axios.post("/level-1-application-approval/return-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application returned successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while returning application!"),
  });
}

export type TSavedDemandNoteData = {
  municipality?: string;
  tehsil?: string;
  district?: string;
  dueDate?: string;
  applicantName?: string;
  layoutName?: string;
  baseAmount?: number;
  interestAccumulated?: number;
  daysPending?: number;
  area?: number;
  mauja?: string;
  khasaraNo?: string;
  constructionArea?: number;
  particular1Rate?: number;
  particular1Total?: number;
  particular2Rate?: number;
  particular2Total?: number;
  totalAmount?: number;
  plotNo?: string;
  scrutinyCharges?: number;
  fireCharges?: number;
  customChargeTitle?: string;
  customChargeAmount?: number;
  conditions?: string;
  /**
   * Set by the backend when a reviewer edited the application after this
   * Demand Note was saved. See getDemandNoteFormDefaults() in
   * src/config/reviewerApplicationEdit.ts.
   */
  applicationEditedAfterSave?: boolean;
  [key: string]: any;
};

export function useSavedDemandNoteDataForLevel1({
  applicationId,
  reviewId,
  isThereAConst,
}: {
  applicationId?: number;
  reviewId?: number;
  isThereAConst?: boolean;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.savedDemandNoteData(
      applicationId,
      reviewId,
      isThereAConst
    ),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{ success: true; data?: TSavedDemandNoteData }>(
        "/level-1-application-approval/get-saved-demand-note-data",
        {
          params: { applicationId, reviewId, isThereAConst },
        }
      ),
  });
}

export function useSavedDemandNoteDataForLevel2({
  applicationId,
  reviewId,
  isThereAConst,
}: {
  applicationId?: number;
  reviewId?: number;
  isThereAConst?: boolean;
}) {
  return useSavedDemandNoteDataForLevel1({ applicationId, reviewId, isThereAConst });
}

export function useSaveDemandNoteDataForLevel1() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
      isThereAConst?: boolean;
    }) =>
      _axios.post(
        "level-1-application-approval/save-demand-note-data",
        data,
        {
          responseType: "blob",
        }
      ),
    onError: () =>
      ErrorToast("Something went wrong while generating sample demand note!"),
  });
}

export function useForwardApplicationForLevel1() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; reviewId: number; isThereAConst?: boolean }) =>
      _axios.post<{ success: true }>(
        "level-1-application-approval/approve-application",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}

// Certificate generation for L1's Phase 2 (Permanent RL) continuation
// review -- mirrors Level 2's certificate step (useLevel2CertificateData /
// useLevel2SaveCertificateData / useGenerateCertificateForLevel2) but
// against L1's own endpoints, so a Level 1 reviewer isn't blocked by
// Level 2's role gate.
export type TCertificateData = Record<string, any>;

export function useLevel1CertificateDataForReview({
  applicationId,
  reviewId,
}: {
  applicationId?: number | string;
  reviewId?: number | string;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.certificateData(applicationId, reviewId),
    enabled:
      !!applicationId &&
      !isNaN(Number(applicationId)) &&
      !!reviewId &&
      !isNaN(Number(reviewId)) &&
      Number(reviewId) > 0,
    select: (d) => d?.data?.data,
    queryFn: () =>
      _axios.get<{ data?: TCertificateData | null; success: true }>(
        "/level-1-application-approval/certificate-data",
        { params: { reviewId, applicationId } }
      ),
  });
}

export function useLevel1SaveCertificateDataForReview() {
  return useMutation({
    mutationFn: (payload: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
    }) =>
      _axios.post<Blob>(
        "/level-1-application-approval/certificate-data",
        payload,
        { responseType: "blob" }
      ),
    onError: () =>
      ErrorToast("Something went wrong while generating certificate!"),
  });
}

export function useGenerateCertificateForLevel1() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; reviewId: number }) =>
      _axios.post<{ success: true }>(
        "level-1-application-approval/generate-certificate",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}
